import { useContext, useCallback } from 'react';
import { Box, MembershipEnum, VoloPassSaleBannerThin } from '@rivallapp/volosports-components';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import styles from '../../../../styles/main.module.scss';
import { RegistrationActions } from '../../../../contexts/RegistrationContext/RegistrationReducer';
import { RegistrationContext } from '../../../../contexts';
import usePaymentSummary from '../hooks/usePaymentSummary';
import { RegisterTypes } from '../tools/constants';
import { useVoloPassDetails, useAlertMessage } from '../../../../hooks';

const { UPDATE_REGISTRATION_STATE } = RegistrationActions;

const cx = classNames.bind(styles);
const RegisterCaption = props => {
  const { image, heading, body, messages, footnotes, hideVoloPassBanner = false } = props;

  const { showError, showSuccess } = useAlertMessage();

  const [registerState, registerDispatch] = useContext(RegistrationContext);

  const {
    league,
    addVpMembership,
    addVpTrial,
    membershipExpires,
    programType,
    registrantType,
    usePaymentSummaryProps,
  } = registerState;

  const { afterExpireDate } = useVoloPassDetails();

  const {
    organization: {
      voloPassTiers: { isActive },
      isVoloPassTrialActive,
    },
  } = league || {};

  const { memberPriceInCents, nonmemberPriceInCents } = usePaymentSummary('non-dropin');

  // copied from PaymentSummary.tsx
  const onStartVpClick = useCallback(() => {
    const { setPricingSelected } = usePaymentSummaryProps;
    try {
      setPricingSelected(MembershipEnum.VOLO_PASS);
      // Check if the user has had a trial previously
      if (isVoloPassTrialActive && !membershipExpires) {
        registerDispatch({
          type: UPDATE_REGISTRATION_STATE,
          update: {
            addVpTrial: true,
          },
        });
      } else {
        registerDispatch({
          type: UPDATE_REGISTRATION_STATE,
          update: {
            addVpMembership: true,
          },
        });
      }
    } catch (e) {
      showError(e);
    } finally {
      showSuccess('Volo Pass added to cart!');
    }
  }, [
    isVoloPassTrialActive,
    membershipExpires,
    registerDispatch,
    showError,
    showSuccess,
    usePaymentSummaryProps,
  ]);

  // Duplicated from `VoloPassBanner.tsx`, which is getting removed in this update.
  const shouldShowBanner =
    !hideVoloPassBanner &&
    isActive &&
    !addVpTrial &&
    !addVpMembership &&
    (!membershipExpires || afterExpireDate) &&
    registrantType !== RegisterTypes.PREPAID_TEAM_CAPTAIN;

  return (
    <div className={cx('register-caption')}>
      <div className={cx(!image && 'text-caption', image && 'image-caption')}>
        {image && <img className={cx('caption-image')} src={image} alt="caption img" />}
        <div className={cx('caption-details')}>
          <h2>{heading}</h2>
          {body?.isArray ? body.map(text => <p key={text}>{text}</p>) : <p>{body}</p>}
        </div>
      </div>
      {shouldShowBanner && (
        <Box marginTop="4">
          <VoloPassSaleBannerThin
            memberPriceInCents={memberPriceInCents}
            nonmemberPriceInCents={nonmemberPriceInCents}
            programType={programType}
            isDryMarket={league?.organization?.is_dry ?? false}
            onPressAddToCart={onStartVpClick}
          />
        </Box>
      )}
      {messages && typeof messages === 'string' && (
        <p className={cx('caption-messages', 'my-3')}>{messages}</p>
      )}
      {messages &&
        typeof messages !== 'string' &&
        messages?.length &&
        messages?.map(message => (
          <p className={cx('caption-messages', 'my-3')} key={message}>
            {messages}
          </p>
        ))}
      {footnotes &&
        footnotes?.length &&
        footnotes?.map(footnote => (
          <p className={cx('caption-messages', 'my-3')} key={footnote}>
            {footnote}
          </p>
        ))}
    </div>
  );
};

RegisterCaption.propTypes = {
  image: PropTypes.string,
  heading: PropTypes.string,
  body: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
  messages: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  footnotes: PropTypes.arrayOf(PropTypes.string),
  hideVoloPassBanner: PropTypes.bool,
};

RegisterCaption.defaultProps = {
  image: undefined,
  heading: '',
  body: '',
  messages: '',
  footnotes: undefined,
  hideVoloPassBanner: false,
};

export default RegisterCaption;
